import 'block.product-buy-buttons'
import { EVENTS } from 'util.events'

const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
  style: 'currency', currency: window.Shopify.currency.active
}).format(cents / 100)

const selectedExtras = root => [...root.querySelectorAll('[data-burner-v2-extra]')].filter(row =>
  row.querySelector('[data-v2-extra-check]').checked
)

async function refreshExtra(row) {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 8000)
  try {
    const response = await fetch(`${window.Shopify.routes.root}products/${row.dataset.handle}.js`, {
      headers: { Accept: 'application/json' }, signal: controller.signal, cache: 'no-store'
    })
    if (!response.ok) throw new Error('Extra unavailable')
    const product = await response.json()
    const select = row.querySelector('[data-v2-extra-variant]')
    for (const option of select.options) {
      const variant = product.variants.find(item => String(item.id) === option.value)
      option.disabled = !variant?.available
      if (variant) {
        option.dataset.price = variant.price
        const title = variant.title === 'Default Title' ? '' : `${variant.title} \u00b7 `
        option.textContent = `${title}${money(variant.price)}${variant.available ? '' : ' \u00b7 Sold out'}`
      }
    }
    return product.variants.find(item => String(item.id) === select.value && item.available)
  } catch {
    throw { description: 'We could not confirm the extra is available. Please try again, or remove it.' }
  } finally {
    clearTimeout(timeout)
  }
}

class IiBurnerV2BuyButtons extends customElements.get('block-buy-buttons') {
  async addVariantToCart() {
    const root = this.closest('[data-burner-v2]')
    const extras = selectedExtras(root)
    if (!extras.length) return super.addVariantToCart()
    const form = this.getFormDataWithSections()
    const item = { id: Number(form.get('id')), quantity: Number(form.get('quantity') || 1) }
    const properties = {}
    for (const [key, value] of form.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key)
      if (match) properties[match[1]] = value
    }
    if (Object.keys(properties).length) item.properties = properties
    if (form.get('selling_plan')) item.selling_plan = form.get('selling_plan')
    const items = [item]
    for (const row of extras) {
      const variant = await refreshExtra(row)
      const quantity = Number(row.querySelector('[data-v2-extra-quantity]').value)
      if (!variant) throw { description: 'The selected extra is sold out. Remove it to buy the burner on its own.' }
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 10) {
        throw { description: 'Choose between 1 and 10 of each selected extra.' }
      }
      items.push({ id: variant.id, quantity })
    }
    root.dispatchEvent(new Event('v2:extras-refresh'))
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items, sections: 'cart-ajax', sections_url: `${location.pathname}${location.search}` })
    })
    const result = await response.json()
    if (!response.ok) throw result
    return result
  }
}
if (!customElements.get('ii-burner-v2-buy-buttons')) customElements.define('ii-burner-v2-buy-buttons', IiBurnerV2BuyButtons)

function setStoryVariant(story, variant, productTitle) {
  if (!story || !variant) return
  story.dataset.finish = variant.title
  const roomGrid = story.querySelector('.burner-v2-room-grid')
  const lifestyleFigures = [...story.querySelectorAll('[data-v2-story-finish]')]
  const activeLifestyle = lifestyleFigures.filter(figure => !figure.dataset.v2StoryFinish || figure.dataset.v2StoryFinish === variant.title)
  for (const figure of lifestyleFigures) figure.hidden = !activeLifestyle.includes(figure)
  const roomFallback = story.querySelector('[data-v2-story-fallback]')
  const roomDetail = story.querySelector('[data-v2-story-detail]')
  if (roomFallback) roomFallback.hidden = activeLifestyle.length > 0
  if (roomDetail) roomDetail.hidden = activeLifestyle.length === 0
  if (roomGrid) roomGrid.dataset.v2HasLifestyle = String(activeLifestyle.length > 0)
  for (const image of story.querySelectorAll('.burner-v2-story-main-image')) {
    if (image.closest('.burner-v2-contents-image')?.dataset.v2StaticImage === 'true') continue
    if (variant.image) {
      image.removeAttribute('srcset')
      image.src = variant.image
    }
    image.alt = variant.title === 'Default Title' ? productTitle : `${productTitle} in ${variant.title}`
  }
  for (const label of story.querySelectorAll('[data-v2-story-variant-label]')) label.textContent = variant.title
  const videoFigure = story.querySelector('[data-v2-video-finish]')
  const fallback = story.querySelector('[data-v2-video-fallback]')
  if (videoFigure && fallback) {
    const showVideo = videoFigure.dataset.v2VideoAllFinishes === 'true' || videoFigure.dataset.v2VideoFinish === variant.title
    videoFigure.hidden = !showVideo
    fallback.hidden = showVideo
    if (!showVideo) videoFigure.querySelector('video')?.pause()
    const caption = fallback.querySelector('figcaption')
    if (caption) caption.textContent = `${variant.title} finish`
  }
}

function init(root) {
  if (root.dataset.v2Ready) return
  root.dataset.v2Ready = 'true'
  const variants = JSON.parse(root.querySelector('[data-v2-variants]').textContent)
  const buy = root.querySelector('ii-burner-v2-buy-buttons')
  const submit = buy?.querySelector('[name="add"]')
  const status = root.querySelector('[data-v2-extra-status]')
  const sticky = root.querySelector('[data-v2-sticky]')
  const stickyButton = root.querySelector('[data-v2-sticky-add]')
  const story = document.querySelector('[data-burner-v2-story]')
  let loadingVariant = false

  const update = () => {
    let total = 0
    for (const row of root.querySelectorAll('[data-burner-v2-extra]')) {
      const selected = row.querySelector('[data-v2-extra-check]').checked
      row.querySelector('.hm20-v2-extra-quantity').hidden = !selected
      const image = row.querySelector('[data-v2-extra-variant]').selectedOptions[0]?.dataset.image
      if (image && row.querySelector('img')) row.querySelector('img').src = image
      if (selected) {
        const quantity = Number(row.querySelector('[data-v2-extra-quantity]').value || 1)
        const price = Number(row.querySelector('[data-v2-extra-variant]').selectedOptions[0]?.dataset.price || 0)
        if (Number.isInteger(quantity) && quantity > 0 && quantity <= 10) total += price * quantity
      }
    }
    const extraTotal = root.querySelector('[data-v2-extra-total]')
    if (extraTotal) extraTotal.textContent = money(total)
    const id = buy?.querySelector('input[name="id"]')?.value
    const variant = variants.find(item => String(item.id) === id)
    const quantity = Number(root.querySelector('[name="quantity"]')?.value || 1)
    if (variant) {
      root.querySelector('[data-v2-sticky-finish]').textContent = variant.title === 'Default Title' ? root.dataset.productTitle : variant.title
      root.querySelector('[data-v2-sticky-price]').textContent = money(variant.price * quantity + total)
    }
    stickyButton.disabled = loadingVariant || !variant?.available || submit?.disabled || submit?.getAttribute('aria-busy') === 'true'
  }
  root.addEventListener('change', async event => {
    update()
    if (!event.target.matches('[data-v2-extra-check]') || !event.target.checked) return
    const row = event.target.closest('[data-burner-v2-extra]')
    event.target.disabled = true
    status.textContent = 'Checking current availability...'
    try {
      const variant = await refreshExtra(row)
      if (!variant) { event.target.checked = false; status.textContent = 'This extra is sold out.' }
      else status.textContent = ''
    } catch (error) { event.target.checked = false; status.textContent = error.description }
    finally { event.target.disabled = false; update() }
  })
  root.addEventListener('input', update)
  root.addEventListener('v2:extras-refresh', update)
  document.addEventListener(`${EVENTS.variantChange}:${root.dataset.sectionId}:${root.dataset.productId}`, event => {
    loadingVariant = !!event.detail.loading
    update()
    if (event.detail.variant && !loadingVariant) {
      const variant = variants.find(item => String(item.id) === String(event.detail.variant.id))
      setStoryVariant(story, variant, root.dataset.productTitle)
    }
  })
  stickyButton.addEventListener('click', () => { if (!stickyButton.disabled) submit.click() })
  if (submit) {
    new MutationObserver(update).observe(submit, { attributes: true, attributeFilter: ['disabled', 'aria-busy'] })
    const syncSticky = () => {
      const rect = submit.getBoundingClientRect()
      sticky.hidden = (rect.bottom > 0 && rect.top < window.innerHeight) || !window.matchMedia('(max-width:768px)').matches
    }
    new IntersectionObserver(syncSticky, { threshold: 0 }).observe(submit)
    window.addEventListener('resize', syncSticky)
    syncSticky()
  }
  const current = variants.find(item => String(item.id) === new URLSearchParams(location.search).get('variant')) || variants.find(item => item.available) || variants[0]
  setStoryVariant(story, current, root.dataset.productTitle)
  update()
}

for (const root of document.querySelectorAll('[data-burner-v2]')) init(root)
document.addEventListener('shopify:section:load', event => { for (const root of event.target.querySelectorAll('[data-burner-v2]')) init(root) })
